/**
 * Browser-side application of an AI `DisclaimerSpec` to a static ad at export
 * time. Two transforms, both transient (the stored ad markup is never mutated):
 *   - visible: splice the shared overlay markup into the ad before it is
 *     rasterized, so it bakes into the captured PNG;
 *   - machine-readable: inject the IPTC/XMP "AI generated" tag into the PNG
 *     bytes after capture.
 *
 * The pure overlay/metadata builders live in `@hightouch/lib/ad-studio/disclaimer`
 * so the (future) motion path can reuse them; only the DOM splicing and Blob
 * handling are browser-specific and live here.
 */

import { extractDimensions } from "@hightouch/lib/ad-studio/html-editor/dimensions";
import { looksLikeHtml } from "@hightouch/lib/ad-studio/html-editor/looks-like-html";
import type { BrandDisclaimerFont } from "@hightouch/lib/ad-studio/disclaimer/brand-defaults";
import {
  buildDisclaimerHtmlOverlay,
  buildDisclaimerSvgOverlay,
} from "@hightouch/lib/ad-studio/disclaimer/overlay";
import { injectXmpIntoPng } from "@hightouch/lib/ad-studio/disclaimer/png-metadata";
import type { DisclaimerSpec } from "@hightouch/lib/ad-studio/disclaimer/types";
import { buildAiDisclosureXmp } from "@hightouch/lib/ad-studio/disclaimer/xmp";

// Mirrors the capture path's fallback canvas size in `capture-html-preview.ts`,
// used only when the markup carries no explicit dimensions.
const FALLBACK_WIDTH = 800;
const FALLBACK_HEIGHT = 600;

/**
 * Return the ad markup with the visible disclosure overlay added. Falls back to
 * the original content (no overlay) if the markup can't be parsed, so an export
 * never fails outright on a malformed ad.
 */
export function applyVisibleDisclaimer(
  content: string,
  spec: DisclaimerSpec,
  font?: BrandDisclaimerFont,
): string {
  if (!spec.visible) return content;
  return looksLikeHtml(content)
    ? applyToHtml(content, spec, font)
    : applyToSvg(content, spec, font);
}

function applyToHtml(
  content: string,
  spec: DisclaimerSpec,
  font?: BrandDisclaimerFont,
): string {
  const doc = new DOMParser().parseFromString(content, "text/html");
  const root = doc.body.firstElementChild;
  if (!(root instanceof HTMLElement)) return content;

  // The overlay is absolutely positioned, so the root needs a positioning
  // context. The static-ad serializer already sets `position:relative`; add it
  // defensively for any markup that doesn't.
  if (!/position\s*:/i.test(root.getAttribute("style") ?? "")) {
    root.style.position = "relative";
  }

  const template = doc.createElement("template");
  template.innerHTML = buildDisclaimerHtmlOverlay({
    position: spec.position,
    text: spec.text,
    font,
  });
  const overlay = template.content.firstElementChild;
  if (overlay) {
    root.appendChild(overlay);
  }
  return root.outerHTML;
}

function applyToSvg(
  content: string,
  spec: DisclaimerSpec,
  font?: BrandDisclaimerFont,
): string {
  const { width, height } = extractDimensions(content);
  const overlay = buildDisclaimerSvgOverlay({
    width: width ?? FALLBACK_WIDTH,
    height: height ?? FALLBACK_HEIGHT,
    position: spec.position,
    text: spec.text,
    font,
  });
  const closeIndex = content.lastIndexOf("</svg>");
  if (closeIndex === -1) return content;
  return content.slice(0, closeIndex) + overlay + content.slice(closeIndex);
}

/**
 * Return a copy of a PNG blob with the IPTC/XMP "AI generated" tag embedded in
 * its metadata.
 */
export async function addAiDisclosureMetadataToPng(blob: Blob): Promise<Blob> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  // Re-wrap in a fresh Uint8Array so the local type is ArrayBuffer-backed
  // (the helper's return type widens to ArrayBufferLike, which BlobPart rejects).
  const withMetadata = new Uint8Array(
    injectXmpIntoPng(bytes, buildAiDisclosureXmp()),
  );
  return new Blob([withMetadata], { type: "image/png" });
}
